import React from "react";
import { Formik, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import { closeModal, openModal } from "react-url-modal";
import { useAppDispatch, useAppSelector } from "@/redux/store";
import { IGame, IApplyGameData } from "@/redux/games/types";
import { fetchApplyGame, fetchGetAppliedGames, fetchGetGamesList, setGames } from "@/redux/games";
import { getProfileInfo } from "@/redux/profile/settings/getProfileInfo/selectors";
import { Button, NameInput, PhoneInput } from "@/components";
import styles from "./JoinGameContent.module.scss";

interface JoinGameContentProps {
  game: IGame;
  onLevelInfoClick?: (level: string) => void;
}

const LEVEL_OPTIONS = [
  { label: "Начинающий", value: "Начинающий" },
  { label: "Средний", value: "Средний" },
  { label: "Продвинутый", value: "Продвинутый" },
];

export const JoinGameContent: React.FC<JoinGameContentProps> = ({ game, onLevelInfoClick }) => {
  const dispatch = useAppDispatch();
  const profileInfo = useAppSelector(getProfileInfo);

  const initialValues = {
    name: profileInfo.profileInfo?.name || "",
    phone: profileInfo.profileInfo?.phone || "",
    level: "",
  };

  const validationSchema = Yup.object({
    name: Yup.string().required("Введите имя"),
    phone: Yup.string()
      .required("Введите телефон")
      .test("phone-length", "Некорректный номер телефона", (value) => {
        return value ? value.replace(/[^+0-9]/g, "").length === 12 : false;
      }),
    level: Yup.string().required("Выберите уровень"),
  });

  const handleSubmit = async (values: typeof initialValues, { setSubmitting }: any) => {
    try {
      const data: IApplyGameData = {
        level: values.level,
        name: values.name.trim(),
        phone: `+${values.phone.replace(/[^0-9]/g, "")}`,
      };

      await dispatch(fetchApplyGame({ id: game.id, data })).unwrap();
      
      // Обновляем списки
      const applied = await dispatch(fetchGetAppliedGames()).unwrap();
      const allGames = await dispatch(fetchGetGamesList()).unwrap();
      const filtered = allGames.filter(
        (g) => !applied.some((appliedGame) => appliedGame.id === g.id)
      );
      dispatch(setGames(filtered));

      closeModal();
      openModal({ name: "successRequestSend" });
    } catch (error) {
      console.error("Error applying to game:", error);
      // TODO: Показать ошибку пользователю
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {(formik) => {
        const isDisabled = formik.isSubmitting || !formik.isValid;

        return (
          <Form className={styles.form}>
            <div className={styles.formSection}>
              <PhoneInput
                name="phone"
                lable="Номер для связи с Вами"
                placeholder="+7 (___) ___-__-__"
              />
              <ErrorMessage component="div" className={styles.errorMessage} name="phone" />
            </div>

            <div className={styles.formSection}>
              <NameInput
                name="name"
                lable="Имя"
                placeholder="Введите имя"
              />
              <ErrorMessage component="div" className={styles.errorMessage} name="name" />
            </div>

            <div className={styles.formSection}>
              <label className={styles.label}>Уровень</label>
              <div className={styles.levelContainer}>
                {LEVEL_OPTIONS.map((level) => (
                  <div key={level.value} className={styles.levelOption}>
                    <label className={styles.levelLabel}>
                      <input
                        type="radio"
                        name="level"
                        value={level.value}
                        checked={formik.values.level === level.value}
                        onChange={(e) => formik.setFieldValue("level", e.target.value)}
                      />
                      <span>{level.label}</span>
                    </label>
                    {onLevelInfoClick && (
                      <button
                        type="button"
                        className={styles.infoButton}
                        onClick={() => onLevelInfoClick(level.value)}
                        aria-label={`Информация об уровне ${level.label}`}
                      >
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 20 20"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <circle cx="10" cy="10" r="9" stroke="currentColor" strokeWidth="1.5" />
                          <path
                            d="M10 7V9M10 13V13.01"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                          />
                        </svg>
                      </button>
                    )}
                  </div>
                ))}
              </div>
              <ErrorMessage component="div" className={styles.errorMessage} name="level" />
            </div>

            <Button
              type="submit"
              disabled={isDisabled}
              newStyles={styles.submitButton}
            >
              {formik.isSubmitting ? "Отправка..." : "Отправить заявку"}
            </Button>
          </Form>
        );
      }}
    </Formik>
  );
};
